<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <h3>{{ $route.meta.title }}</h3>
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>审单考核管理</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: '目标项目设定' }">目标项目列表</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <layout-panel :collapse="true">
      <template slot="header">搜索</template>
      <el-form :model="search"
               label-width="96px"
               label-position="right"
               size="medium"
               @submit.native.prevent="onSearch()"
               ref="searchForm">
        <div class="re-el-form__flex">
          <el-form-item label="日期" prop="plan_date">
            <el-date-picker
              v-model="search.plan_date"
              type="date"
              placeholder="请选择日期"
              value-format="yyyy-MM-dd"
              class="width-max"
              clearable>
            </el-date-picker>
          </el-form-item>
          <el-form-item label="实习生" prop="intern_user_id">
            <remote-select
              v-model="search.intern_user_id"
              placeholder="请输入实习生姓名搜索"
              clearable
              class="width-max"
              id-key="id"
              label-key="name"
              value-key="id"
              keyword-key="name"
              :query-request="getInternUserTable"
              :find-request="getTargetInternUserDetail">
            </remote-select>
          </el-form-item>
        </div>

        <el-form-item>
          <el-button type="primary"
                     icon="el-icon-search"
                     native-type="submit"
                     size="mini">搜索
          </el-button>
          <el-button @click="resetForm('searchForm')"
                     size="mini">重置
          </el-button>
          <el-button @click="onExportSearch"
                     size="mini">导出
          </el-button>
        </el-form-item>
      </el-form>
    </layout-panel>

    <layout-panel>
      <template slot="header">列表</template>
      <template slot="tools">
        <el-button type="primary"
                   size="small"
                   icon="el-icon-refresh"
                   @click="onRefresh()"></el-button>
      </template>
      <div class="margin b15">
        <div class="project-info">
          <span class="info-item">项目状态: {{ formatProjectStatus(projectInfo.status) }}</span>
          <span class="info-item">项目编号: {{ projectInfo.project_number || '-' }}</span>
          <span class="info-item">项目名称: {{ projectInfo.project_name || '-' }}</span>
        </div>
        <div class="action-buttons">
          <el-button type="primary"
                     size="mini"
                     icon="el-icon-plus"
                     @click="handleAdd">添加目标值
          </el-button>
          <el-button type="warning"
                     size="mini"
                     @click="handleBatchSet">批量设置目标值
          </el-button>
        </div>
      </div>

      <el-table :data="table"
                stripe
                highlight-current-row
                border
                style="width: 100%">

        <el-table-column prop="id"
                         label="ID"
                         width="80">
        </el-table-column>
        <el-table-column label="实习生"
                         min-width="180">
          <template slot-scope="scope">
            {{ scope.row.intern_name || '-' }} / {{ scope.row.intern_user_id != null && scope.row.intern_user_id !== '' ? scope.row.intern_user_id : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="plan_date"
                         label="日期"
                         width="120"
                         align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.plan_date) }}
          </template>
        </el-table-column>
        <el-table-column prop="plan_order_count"
                         label="审单目标次数"
                         width="150"
                         align="center">
          <template slot-scope="scope">
            {{ scope.row.plan_order_count || 0 }}
          </template>
        </el-table-column>
        <el-table-column prop="first_audit_count"
                         label="初审次数"
                         width="120"
                         align="center">
          <template slot-scope="scope">
            {{ scope.row.first_audit_count != null ? scope.row.first_audit_count : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="multi_audit_count"
                         label="复审次数"
                         width="120"
                         align="center">
          <template slot-scope="scope">
            {{ scope.row.multi_audit_count != null ? scope.row.multi_audit_count : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="是否达标"
                         width="100"
                         align="center">
          <template slot-scope="scope">
            {{ scope.row.audit_times_count >= scope.row.plan_order_count ? '是' : '否' }}
          </template>
        </el-table-column>
        <el-table-column label="目标达成率"
                         width="100"
                         align="center">
          <template slot-scope="scope">
            {{ scope.row.plan_order_count > 0 ? Math.min(scope.row.audit_times_count / scope.row.plan_order_count * 100, 100).toFixed(2) + '%' : '0.00%' }}
          </template>
        </el-table-column>
        <el-table-column prop="updated_at"
                         label="修改时间"
                         width="180"
                         align="center">
        </el-table-column>
        <el-table-column label="操作"
                         width="120"
                         fixed="right">
          <template slot-scope="scope">
            <el-button type="text"
                       size="small"
                       @click="handleEdit(scope.row)">修改
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination background
                       @size-change="handleSizePageChange"
                       @current-change="handleCurrentPageChange"
                       :current-page="pagination.current_page"
                       :page-sizes="[20, 50, 100, 200]"
                       :page-size="pagination.per_page"
                       layout="total, sizes, prev, pager, next, jumper"
                       :total="pagination.total">
        </el-pagination>
      </div>
    </layout-panel>

    <!-- 添加目标值弹窗 -->
    <target-value-form-dialog ref="formDialog"
                              :project-id="projectId"
                              :audit-project-range-id="auditProjectRangeId"
                              @submitted="onFormSubmitted">
    </target-value-form-dialog>

    <!-- 批量设置弹窗 -->
    <batch-target-dialog ref="batchDialog"
                         :project-id="projectId"
                         :audit-project-range-id="auditProjectRangeId"
                         @submitted="onFormSubmitted">
    </batch-target-dialog>

    <!-- 修改审单目标次数弹窗 -->
    <el-dialog title="修改审单目标次数"
               :visible.sync="editDialogVisible"
               :close-on-click-modal="false"
               width="440px"
               @closed="onEditDialogClosed">
      <el-form ref="editFormRef"
               :model="editForm"
               label-width="100px"
               size="medium">
        <el-form-item label="实习生">
          <span>{{ editForm.intern_name || '-' }}</span>
        </el-form-item>
        <el-form-item label="日期">
          <span>{{ formatDate(editForm.plan_date) }}</span>
        </el-form-item>
        <el-form-item label="审单目标次数"
                      prop="plan_order_count">
          <el-input-number
            v-model="editForm.plan_order_count"
            :min="0"
            :precision="0"
            controls-position="right"
            class="width-max">
          </el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary"
                   :loading="editSubmitting"
                   @click="handleEditConfirm">确认
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import table from '@core/mixins/table'
import TargetValueFormDialog from '../components/TargetValueFormDialog.vue'
import BatchTargetDialog from '../components/BatchTargetDialog.vue'

export default {
  mixins: [table],
  components: {
    TargetValueFormDialog,
    BatchTargetDialog,
  },
  data () {
    return {
      projectId: null,
      auditProjectRangeId: null,
      targetSettingId: null,
      search: {
        plan_date: null,
        intern_user_id: null,
        audit_project_range_id: null,
      },
      searchFieldType: {
        intern_user_id: Number,
        audit_project_range_id: Number,
      },
      table: [],
      projectInfo: {},
      editDialogVisible: false,
      editSubmitting: false,
      editForm: {
        id: null,
        intern_name: '',
        plan_date: '',
        plan_order_count: 0,
      },
      exportHeader: [
        {
          key: 'id',
          text: 'ID',
        },
        {
          key: 'intern_name',
          text: '实习生',
        },
        {
          key: 'plan_date',
          text: '日期',
        },
        {
          key: 'plan_order_count',
          text: '目标值单量',
        },
        {
          key: 'first_audit_count',
          text: '初审次数',
        },
        {
          key: 'multi_audit_count',
          text: '复审次数',
        },
        {
          key: 'audit_times_count',
          text: '审核总次数',
        },
        {
          key: 'is_target_met',
          text: '是否达标',
        },
        {
          key: 'target_rate',
          text: '目标达成率',
        },
        {
          key: 'updated_at',
          text: '修改时间',
        },
      ],
    }
  },
  created () {
    // 从路由参数获取项目ID
    this.projectId = this.$route.params.projectId || this.$route.query.projectId
    this.auditProjectRangeId = this.$route.params.audit_project_range_id || this.$route.query.audit_project_range_id
    this.syncAuditRangeToSearch()
    if (!this.projectId) {
      this.$message.error('缺少项目ID参数')
      this.$router.back()
      return
    }
    // 从路由参数获取目标设置ID
    this.targetSettingId = this.$route.query.targetSettingId
    // 获取项目详情
    this.getProjectInfo()
  },
  methods: {
    /**
     * 将路由上的 audit_project_range_id 同步到 search，保证搜索/分页请求始终携带
     */
    syncAuditRangeToSearch () {
      const v = this.auditProjectRangeId
      this.search.audit_project_range_id = v != null && v !== '' ? Number(v) : null
    },
    /**
     * 获取实习生列表
     */
    getInternUserTable (params) {
      return request('queryInternTableApi', params)
    },
    /**
     * 获取实习生详情
     */
    getTargetInternUserDetail (params) {
      return request('getTargetInternUserDetail', params)
    },
    /**
     * 获得表格数据
     * @param {*} params
     */
    getTableData (params) {
      return request.page('getTargetValueTable', params)
    },
    /**
     * 获取项目信息
     */
    getProjectInfo () {
      if (!this.projectId) {
        return
      }
      request.common('getProjectDetail', {id: this.projectId}).then(({data}) => {
        this.projectInfo = {
          status: data.status,
          project_number: data.number,
          project_name: data.name,
        }
      }).catch(() => {
        this.$message.error('获取项目详情失败')
      })
    },
    /**
     * 格式化项目状态
     */
    formatProjectStatus (status) {
      if (!status && status !== 0) {
        return '-'
      }
      const projectStatusMap = this.projectStatusMap
      if (!projectStatusMap || !Array.isArray(projectStatusMap)) {
        return status
      }
      const statusItem = projectStatusMap.find(item => item.value === status * 1)
      return statusItem ? statusItem.label : status
    },
    /**
     * 导出搜索
     */
    onExportSearch () {
      this.$export(this.$route.meta.title, {
        fileHeader: this.exportHeader,
        total: this.pagination.total,
        request: this.getTableData,
        requestParams: this.search,
        getResponseData: (data) => {
          if (data && data.data) {
            data.data = this.handleExportData(data.data)
          }
          return data
        }
      })
    },
    /**
     * 处理导出数据
     */
    handleExportData (data) {
      if (!data || !Array.isArray(data)) {
        return []
      }
      return data.map(row => {
        return {
          ...row,
          plan_date: this.formatDate(row.plan_date),
          is_target_met: row.audit_times_count >= row.plan_order_count ? '是' : '否',
          target_rate: `${(row.plan_order_count > 0 ? Math.min(row.audit_times_count / row.plan_order_count * 100, 100) : 0).toFixed(2)}%`,
        }
      })
    },
    /**
     * 重置表单
     */
    resetForm (formName) {
      this.$refs[formName].resetFields()
      this.syncAuditRangeToSearch()
      this.onSearch()
    },
    /**
     * 格式化日期
     */
    formatDate (date) {
      if (!date) return '-'
      const d = new Date(date)
      if (isNaN(d.getTime())) {
        return date
      }
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`
    },
    /**
     * 格式化日期时间
     */
    formatDateTime (datetime) {
      if (!datetime) return '-'
      const d = new Date(datetime)
      if (isNaN(d.getTime())) {
        return datetime
      }
      return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },
    /**
     * 添加目标值
     */
    handleAdd () {
      this.$refs.formDialog.show()
    },
    /**
     * 批量设置目标值
     */
    handleBatchSet () {
      this.$refs.batchDialog.show()
    },
    /**
     * 打开修改审单目标次数弹窗
     */
    handleEdit (row) {
      this.editForm = {
        id: row.id,
        intern_name: row.intern_name,
        plan_date: row.plan_date,
        plan_order_count: row.plan_order_count != null ? Number(row.plan_order_count) : 0,
      }
      this.editDialogVisible = true
    },
    /**
     * 弹窗内确认修改
     */
    handleEditConfirm () {
      this.editSubmitting = true
      const submitData = {
        id: this.editForm.id,
        plan_order_count: this.editForm.plan_order_count,
        updater: this.user.name || this.user.username || '',
      }
      request.common('updateTargetValue', submitData).then(() => {
        this.$message.success('修改成功')
        this.editDialogVisible = false
        this.onRefresh()
      }).finally(() => {
        this.editSubmitting = false
      })
    },
    /**
     * 修改弹窗关闭后重置表单
     */
    onEditDialogClosed () {
      this.editForm = {
        id: null,
        intern_name: '',
        plan_date: '',
        plan_order_count: 0,
      }
    },
    /**
     * 表单提交成功后的回调
     */
    onFormSubmitted () {
      this.onRefresh()
    },
  },
  computed: {
    ...Vuex.mapState({
      user: state => state.login.user,
      projectStatusMap: state => state.customerProjectcost.options.status,
    }),
  },
}
</script>

<style lang="scss" scoped>
.pagination {
  margin-top: 20px;
  text-align: right;
}

.project-info {
  display: inline-block;
  margin-right: 20px;

  .info-item {
    margin-right: 20px;
    font-size: 14px;
    color: #606266;
  }
}

.action-buttons {
  display: inline-block;
}
</style>




// WEBPACK FOOTER //
// src/views/order-review-target-management/pages/TargetSettingTable.vue